import { Tooltip } from "@mui/material";
import { Store } from "../../../types";
import formatDateToLocal from "../../../utils/formatDateToLocal";
import { useAuth } from "../../../utils/authorization";
import ClockIcon from "../../../assets/ClockIcon";

type Props = {
    store: Store;
};

const formatCount = (count?: number) => count ?? 'na';

type Stat = { label: string; value?: number; tone: 'index' | 'shopify' | 'system' };

function StatGroup({ title, stats }: { title: string; stats: Stat[] }) {
    return (
        <div className="report-header__group">
            <span className="report-header__label">{title}</span>
            <div className="report-header__stats">
                {stats.map((stat) => (
                    <div key={stat.label} className="report-header__stat">
                        <span>{stat.label}</span>
                        <b className={stat.tone}>{formatCount(stat.value)}</b>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function StoreReportHeader({ store }: Props) {
    const { user } = useAuth();
    const useLocal = user?.localTimezone === 'active';
    const storeReport = store.storeReport ? JSON.parse(store.storeReport) : null;
    const products = storeReport?.Products;
    const collections = storeReport?.Collections;

    const lastRefresh = () => {
        if (store.lastUpdate === undefined) return 'Not available at this moment';
        if (!store.lastUpdate) return `Not available ${useLocal ? 'Local' : 'UTC'}`;
        const date = useLocal ? formatDateToLocal(store.lastUpdate) : store.lastUpdate;
        return `${date.replace(/T/, ' ').replace(/\..+/, '')} ${useLocal ? 'Local' : 'UTC'}`;
    };

    const refreshLabel = lastRefresh();

    return (
        <div className="report-header">
            <span className="report-header__title">Sync results</span>
            <StatGroup
                title="PRODUCTS"
                stats={[
                    { label: 'Index', value: products?.IndexCount, tone: 'index' },
                    { label: 'Shopify', value: products?.ShopifyCount, tone: 'shopify' },
                    { label: 'RetailSys', value: products?.MirrorCount, tone: 'system' },
                ]}
            />
            <StatGroup
                title="COLLECTIONS"
                stats={[
                    { label: 'Shopify', value: collections?.ShopifyCount, tone: 'shopify' },
                    { label: 'RetailSys', value: collections?.MirrorCount, tone: 'system' },
                ]}
            />
            <div className="report-header__refresh">
                <span className="report-header__refresh-text">
                    Last refreshed <span>{refreshLabel}</span>
                </span>
                <Tooltip title={`Last refreshed ${refreshLabel}`}>
                    <span className="report-header__clock">
                        <ClockIcon />
                    </span>
                </Tooltip>
            </div>
        </div>
    );
}
